import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { normalizeString } from '../helpers'
import { useNavigate } from 'router'
import { useSites } from 'store'
import { useActiveEquipment, useFilters } from '../../store'

const PAGE_SIZE = 20
const SUPERVISED_ACCESS_POINT_STATUSES = ['up', 'down', 'hibernation']

const isSupervisedAccessPoint = (equipment) =>
  SUPERVISED_ACCESS_POINT_STATUSES.includes(equipment.available_level)

const useSsid = ({ equipments, sortApi }) => {
  const [page, setPage] = useState(0)
  const [{ verbatim }] = useFilters()
  const { siteId } = useParams()
  const [{ selected }] = useSites()
  const navigate = useNavigate()
  const [activeEquipment, setActiveEquipment] = useActiveEquipment()


  const ssids = useMemo(() => {
    const supervisedAccessPointCountBySite = equipments.reduce((acc, equipment) => {
      if (!isSupervisedAccessPoint(equipment) || !equipment.site?.id) return acc
      return {
        ...acc,
        [equipment.site.id]: (acc[equipment.site.id] || 0) + 1,
      }
    }, {})

    const getSiteTotalEquipments = (equipment) =>
      supervisedAccessPointCountBySite[equipment.site?.id] || 0

    return equipments.reduce((acc, equipment) => {
      (equipment.ssid_names || [])
        .filter(ssid => !/^mesh-[a-zA-Z\d]{8}/.test(ssid))
        .forEach((name) => {
          const idxSsid = acc.findIndex(({ ssid_name }) => ssid_name === name)
          const siteTotalEquipments = getSiteTotalEquipments(equipment)
          if (idxSsid === -1) {
            acc = [
              ...acc,
              {
                ssid_name: name,
                connected_stations: equipment.connected_stations,
                total_reachable_equipments: equipment.available_level === 'up' ? 1 : 0,
                total_equipments: siteTotalEquipments,
                sites: [
                  {
                    id: equipment.site?.id,
                    sf_id: equipment.site?.sf_id,
                    name: equipment.site?.name,
                    connected_stations: equipment.connected_stations,
                    reachable_equipments: equipment.available_level === 'up' ? 1 : 0,
                    total_equipments: siteTotalEquipments,
                  },
                ],
              },
            ]
          } else {
            let ssid = {
              ...acc[idxSsid],
              connected_stations:
                acc[idxSsid].connected_stations + equipment.connected_stations,
              total_reachable_equipments: acc[idxSsid].total_reachable_equipments + (equipment.available_level === 'up' ? 1 : 0),
            }
            const idxSite = ssid.sites.findIndex(
              ({ id }) => equipment.site?.id === id,
            )
            if (idxSite === -1) {
              ssid = {
                ...ssid,
                total_equipments: ssid.total_equipments + siteTotalEquipments,
                sites: [
                  ...ssid.sites,
                  {
                    id: equipment.site?.id,
                    name: equipment.site?.name,
                    connected_stations: equipment.connected_stations,
                    reachable_equipments: equipment.available_level === 'up' ? 1 : 0,
                    total_equipments: siteTotalEquipments,
                  },
                ],
              }
            } else {
              ssid.sites[idxSite] = {
                ...ssid.sites[idxSite],
                connected_stations:
                  ssid.sites[idxSite].connected_stations +
                  equipment.connected_stations,
                reachable_equipments: ssid.sites[idxSite].reachable_equipments + (equipment.available_level === 'up' ? 1 : 0),
              }
            }
            acc[idxSsid] = ssid
          }
        })
      return acc
    }, [])
  }, [equipments])

  const filteredSsids = useMemo(() => {
    if (!verbatim.length) {
      return ssids
    }
    return ssids.filter((item) => {
      return (
        ['name', 'site_name'].filter((column) => {
          if (column === 'site_name') {
            return item.sites.findIndex(({ name }) => 
              normalizeString(name).includes(normalizeString(verbatim))
            ) > -1
          }
          return normalizeString(item.ssid_name).includes(normalizeString(verbatim))
        }).length > 0
      )
    })
  }, [ssids, verbatim])
  const selection = useMemo(() => {
    if (!selected.length) {
      return sortApi.sort(filteredSsids)
    }
    return sortApi
      .sort(filteredSsids)
      .filter(
        (ssid) =>
          ssid.sites &&
          selected.findIndex(({ sf_id }) => ssid.sites.find((site) => site.sf_id === sf_id)) > -1,
      )
  }, [filteredSsids, selected, sortApi.orderBy, sortApi.orderDirection])

  return {
    active: (ssid) => {
      if (activeEquipment.id === `${ssid.name}${ssid.site.id}`) {
        setActiveEquipment({})
      }
    },
    focus: async (ssid) => {
      if (ssid.site.sf_id !== siteId) {
        navigate({
          pathname: ssid.site.sf_id,
          siteId: ssid.site.sf_id,
        })
      }
    },
    selection: useMemo(
      () => selection.slice(0, (page + 1) * PAGE_SIZE),
      [selection, page, sortApi.orderBy, sortApi.orderDirection],
    ),
    ssids: selection,
    count: useMemo(() => selection.length, [selection]),
    fetchNextPage: () => setPage((prev) => prev + 1),
    shouldFetchNextPage: useMemo(
      () => (page + 1) * PAGE_SIZE < selection.length,
      [page, selection],
    ),
  }
}

export default useSsid
